import {
  Coding,
  Encounter,
  EncounterStatusEnum,
  Patient,
  PatientGenderEnum,
  QuestionnaireResponse,
  Task,
  TaskIntentEnum,
  TaskStatusEnum,
  type QuestionnaireResponse as QuestionnaireResponseType,
} from '@core/fhir-r4/fhir';
import type { TriageWorkflowStepIdentity } from '@core/entities/Triage';
import { TriageWorkflowTaskInputs } from '@core/entities/TriageWorkflowTaskInputs';


export interface PreTriageTaskPatientContext {
  patientId?: string;
  birthDate?: string;
  gender?: 'male' | 'female';
}

export class R4PreTriageApplyTaskBuilder {
  public buildInitial(): Task {
    return new Task({
      status: TaskStatusEnum.REQUESTED,
      intent: TaskIntentEnum.ORDER,
    });
  }

  public buildContinuation(
    encounterId: string,
    questionnaireResponse: QuestionnaireResponseType,
    stepIdentity: TriageWorkflowStepIdentity,
    patientContext?: PreTriageTaskPatientContext,
  ): Task {
    const encounter = new Encounter({
      id: encounterId,
      status: EncounterStatusEnum.IN_PROGRESS,
      class: new Coding({
        system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
        code: 'AMB',
        display: 'ambulatory',
      }),
    });

    const responsePayload = new QuestionnaireResponse({
      ...questionnaireResponse,
      id: questionnaireResponse.id?.trim() || 'pre-triage-response',
    });

    const patient = this.buildPatient(patientContext);
    const contained = patient
      ? [encounter, patient, responsePayload]
      : [encounter, responsePayload];

    const task = new Task({
      status: TaskStatusEnum.REQUESTED,
      intent: TaskIntentEnum.ORDER,
      encounter: {
        reference: `#${encounterId}`,
      },
      ...(patient ? { for: { reference: `#${patient.id}` } } : {}),
      contained,
    });

    (task as unknown as { input?: Array<Record<string, unknown>> }).input = [
      this.createWorkflowInput(TriageWorkflowTaskInputs.StepSequenceCode, stepIdentity.stepSequence),
      this.createWorkflowInput(TriageWorkflowTaskInputs.JourneyRevisionCode, stepIdentity.journeyRevision),
      this.createWorkflowInput(TriageWorkflowTaskInputs.QuestionnaireEventSequenceCode, stepIdentity.questionnaireEventSequence),
    ];

    (task as Task & { focus?: { reference: string } }).focus = {
      reference: `#${responsePayload.id}`,
    };

    return task;
  }

  private buildPatient(patientContext?: PreTriageTaskPatientContext): Patient | undefined {
    if (!patientContext?.birthDate && !patientContext?.gender && !patientContext?.patientId) {
      return undefined;
    }

    const gender = patientContext.gender === 'male'
      ? PatientGenderEnum.MALE
      : patientContext.gender === 'female'
        ? PatientGenderEnum.FEMALE
        : undefined;

    return new Patient({
      id: patientContext.patientId?.trim() || 'triage-patient',
      birthDate: patientContext.birthDate,
      gender,
    });
  }

  private createWorkflowInput(code: string, value: number): Record<string, unknown> {
    return {
      type: {
        coding: [
          new Coding({
            system: TriageWorkflowTaskInputs.CodingSystem,
            code,
          }),
        ],
      },
      valueInteger: value,
    };
  }
}